// =====================================================================
// PostHog initialization, lazily loaded.
// ---------------------------------------------------------------------
// posthog-js is ~190 KB (63 KB gzipped). Static-importing it would
// bloat the landing-page TTI for a visitor who hasn't even accepted
// cookies yet. Instead we dynamic-import the SDK the first time
// initPostHog() is called, and the import resolves in the background
// while the rest of the app keeps painting.
//
// Capture is OPT-OUT by default (GDPR-friendly). The CookieBanner
// flips opt-in once the visitor accepts cookies; refusing leaves
// capture disabled but still allows local app behaviour (auth,
// routing, etc.) to function.
// =====================================================================

const KEY  = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

let _posthog = null;
let _bootPromise = null;

/**
 * Kick off PostHog initialization. Returns a promise that resolves to
 * the booted PostHog instance, or `null` if PostHog is disabled in
 * this environment (no key, SSR, etc.).
 *
 * Safe to call repeatedly, subsequent calls return the same promise.
 */
export function initPostHog() {
  if (_posthog) return Promise.resolve(_posthog);
  if (_bootPromise) return _bootPromise;
  if (typeof window === "undefined" || !KEY) {
    return Promise.resolve(null);
  }

  _bootPromise = import("posthog-js").then(({ default: posthog }) => {
    posthog.init(KEY, {
      api_host: HOST,
      person_profiles: "identified_only",
      capture_pageview: false,        // we drive page views from the router
      capture_pageleave: true,
      opt_out_capturing_by_default: true,
      // Privacy-friendly defaults, flip these on a per-project basis.
      disable_session_recording: true,
      autocapture: false,
      loaded: (ph) => {
        // Re-apply the persisted consent decision on every load.
        const consent = localStorage.getItem("lm-cookie-consent");
        if (consent === "accepted") ph.opt_in_capturing();
        else ph.opt_out_capturing();
      },
    });
    _posthog = posthog;
    return posthog;
  });

  return _bootPromise;
}

/**
 * Synchronous accessor, returns the PostHog instance if it has
 * already finished booting, otherwise `null`. Callers that want to
 * fire-and-forget events should use the AnalyticsContext, which
 * awaits the boot promise internally.
 */
export function getPostHog() {
  return _posthog;
}
